import { postDesignAndAwait } from '@frontend/store/creative-studio/await-design-job';
import { saveImageToAiStudio } from '@frontend/store/creative-studio/ai-studio-library';
import { normalizeBrowserUrl } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { unwrapShareUrl } from '@frontend/store/creative-studio/share-url';
import {
  buildImageEditPrompt,
  imageEditBaseName,
  imageEditLibraryName,
  resolveImageEditSource,
} from '@frontend/store/creative-studio/image-edit';

const EDIT_FAILED = "Couldn't update the image. Please try again.";

function parseArgs($message) {
  try {
    const raw = $message.function_call.arguments;

    return (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
  } catch (e) {
    return {};
  }
}

/**
 * edit_image: edit an image inline with Nano Banana Pro instead of sending the
 * user to the Image Editor (POR-14685). One assistant message carries the whole
 * edit — "Updating your image…" over the dimmed source, then the result — and
 * the result is saved to the "AI Studio" folder so its link outlives a deploy.
 *
 * `dispatch` is sendMessage's conversation guard: after "New" every write below
 * resolves to nothing instead of landing in the new chat.
 */
export async function handleEditImage($message, { state, commit, dispatch } = {}) {
  const args = parseArgs($message);
  const instruction = String(args.instruction || '').trim();

  if (!instruction) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'What would you like to change in the image?',
    });
    return;
  }

  const messages = state?.messages || [];
  const lastUserMsg = [...messages].reverse().find((m) => m && m.role === 'user');
  const source = resolveImageEditSource({
    imageIndex: Number.isInteger(args.image_index) ? args.image_index : null,
    savedAttachments: $message.savedAttachments || [],
    userAttachments: lastUserMsg?.attachments || [],
    messages,
  });

  if (!source) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Attach, paste, or select the image you want to edit.',
    });
    return;
  }

  const edit = {
    status: 'editing',
    sourceUrl: normalizeBrowserUrl(source.url),
    sourceName: source.name || '',
    instruction,
  };

  const saved = await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: 'Updating your image…',
    widgets: [{ type: 'image_edit', id: 'image_edit' }],
    _imageEdit: edit,
  });

  if (!saved || !saved.id) return;

  // Find the message again at write time: messages may have been added since.
  const update = (patch) => {
    const index = (state.messages || []).findIndex((m) => m && m.id === saved.id);

    return index === -1 ? null : dispatch('updateAndSaveMessage', { index, patch });
  };

  commit('SET_PROCESSING_CHAT_MESSAGE', true);

  try {
    const images = await postDesignAndAwait('/networks/design', {
      image_prompt: buildImageEditPrompt(instruction),
      // A picked Salsify/stock image is a /share link around an external URL:
      // hand the backend the URL itself, or it drops the base. (POR-13385)
      reference_images_urls: [unwrapShareUrl(edit.sourceUrl)],
      product_images_names: [],
      force_edit_high_res: true,
      keep_source_frame: true,
      source: 'creative_studio_chat',
    });
    const tempUrl = Array.isArray(images) ? (images[0]?.image_url ?? images[0]) : null;

    if (!tempUrl) {
      throw new Error('No image URL returned');
    }

    const library = await saveImageToAiStudio({ url: tempUrl, name: imageEditLibraryName(source.name) });
    const resultUrl = normalizeBrowserUrl((library && library.url) || tempUrl);

    await update({
      content: "Here's your updated image.",
      attachments: [{ type: 'url', payload: resultUrl, name: `${imageEditBaseName(source.name)} edited` }],
      _imageEdit: { ...edit, status: 'ready', resultUrl },
    });
  } catch (e) {
    console.warn('Image edit failed:', e);
    await update({
      content: "I couldn't update the image.",
      _imageEdit: { ...edit, status: 'error', error: EDIT_FAILED },
    });
  } finally {
    commit('SET_PROCESSING_CHAT_MESSAGE', false);
  }
}
